// The token warning on the Billing page, for a customer on the token ledger (go-live plan,
// Tony 31 Oct 2026): at 80% and 100% of the plan's tokens, and whenever tokens are owed.
// The worst case wins: paused live charts, then owing, then the percentages.

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { TokenRunOut } from '../../types/subscription';

const FLOOR = 'Charts already on your boat still draw.';

export interface TokenAlertInput {
  /** Share of this period's plan tokens used. */
  percent: number;
  /** Tokens left, top-ups included. */
  remaining: number;
  topup: number;
  runOut: TokenRunOut;
}

export interface TokenAlertMessage {
  level: 'warn' | 'owing' | 'paused';
  title: string;
  text: string;
}

const n = (v: number) => v.toLocaleString('en-NZ');

function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
}

export function tokenAlert({ percent, remaining, topup, runOut }: TokenAlertInput): TokenAlertMessage | null {
  if (runOut.live_paused && runOut.live_needs_consent) {
    // Not stopped for good: the Scout asks once whether to use the overdraft (Tony, 3 Nov 2026).
    const od = runOut.overdraft_left != null ? `your ${n(runOut.overdraft_left)}-token overdraft` : 'your overdraft';
    return {
      level: 'paused',
      title: 'Out of tokens',
      text: `Paid live charts are paused. On your Scout, buy tokens or choose to use ${od}. ${FLOOR}`,
    };
  }
  if (runOut.live_paused) {
    return {
      level: 'paused',
      title: 'Out of tokens',
      text: `Paid live charts and new downloads are paused until you top up. ${FLOOR}`,
    };
  }
  if (runOut.owed > 0) {
    const left =
      runOut.policy === 'overdraft' && runOut.overdraft_left != null && runOut.amount != null
        ? ` You have ${n(runOut.overdraft_left)} of your ${n(runOut.amount)}-token overdraft left.`
        : runOut.policy === 'grace' && runOut.grace_ends_at
          ? ` Live charts stop on ${day(runOut.grace_ends_at)} unless you top up.`
          : '';
    return {
      level: 'owing',
      title: `You owe ${n(runOut.owed)} tokens`,
      text: `They come off your next allowance or top-up first.${left}`,
    };
  }
  if (percent >= 100) {
    return {
      level: 'warn',
      title: "You've used all your plan's tokens for this period",
      text: topup > 0 ? `You have ${n(topup)} top-up tokens left.` : `You have ${n(remaining)} tokens left. Top up below to keep going.`,
    };
  }
  if (percent >= 80) {
    return {
      level: 'warn',
      title: `You've used ${Math.floor(percent)}% of your plan's tokens`,
      text: `${n(remaining)} tokens left. Top up below if you need more before your next allowance.`,
    };
  }
  return null;
}

export function TokenAlert(props: TokenAlertInput) {
  const alert = tokenAlert(props);
  if (!alert) return null;
  const red = alert.level !== 'warn';
  return (
    <div
      role="alert"
      className={clsx(
        'flex items-start gap-3 p-4 rounded-xl border mb-6',
        red ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/20',
      )}
    >
      <ExclamationTriangleIcon className={clsx('w-5 h-5 shrink-0 mt-0.5', red ? 'text-red-400' : 'text-amber-500')} />
      <div className="text-sm">
        <p className={clsx('font-medium', red ? 'text-red-300' : 'text-amber-400')}>{alert.title}</p>
        <p className="text-slate-300 mt-1">{alert.text}</p>
      </div>
    </div>
  );
}
